import {
  ApiResponse,
  API_RESPONSE_CODES,
} from "../../../api/types/ApiResponse";
import DefaultExceptionTypes from "core/exceptions/DefaultExceptionTypes";

export interface ApiResponseResult<T> {
  success: boolean;
  data?: T;
  message: string;
  code: string;
}

export class ApiResponseHandler {
  /**
   * Handles the standardized API response format
   * @param response - The raw API response
   * @returns Processed response with success status and data
   */
  static handleResponse<T>(response: ApiResponse<T>): ApiResponseResult<T> {
    const { code, message, data } = response;

    if (code === "00") {
      return {
        success: true,
        data,
        message,
        code,
      };
    }

    if (code === "01") {
      return {
        success: false,
        message,
        code,
      };
    }

    // Handle unexpected response codes
    return {
      success: false,
      message: `Unexpected response code: ${code}`,
      code,
    };
  }

  /**
   * Checks if the response indicates success
   */
  static isSuccess(response: ApiResponse): boolean {
    return response.code === API_RESPONSE_CODES.SUCCESS;
  }

  /**
   * Checks if the response indicates an error
   */
  static isError(response: ApiResponse): boolean {
    return response.code === API_RESPONSE_CODES.ERROR;
  }

  /**
   * Extracts error message from response
   */
  static getErrorMessage(response: ApiResponse): string {
    return response.message || "An error occurred";
  }

  /**
   * Creates an exception from an error response
   */
  static createExceptionFromResponse(response: ApiResponse): {
    type: DefaultExceptionTypes;
    message: string;
    body?: string;
  } {
    // Create an Exception object that implements the interface
    return {
      type: DefaultExceptionTypes.BAD_REQUEST,
      message: response.message || "API Error",
      body: response.code,
    };
  }
}
